import { type FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBillingPlanPaidCheck } from "../billing/useSubscription";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { StatusPlaceholder } from "../components/StatusPlaceholder";
import { Tabs } from "../components/Tabs";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { EmptyPage } from "../layout/EmptyLayout";
import { Svg } from "../svg";
import { AIVisibilityTab } from "./ai-visibility/AIVisibilityTab";
import { SettingsTab } from "./SettingsTab";
import "./PageSeoKeywords.css";

const PageSeoTabs: FC = () => {
	const [activeTab, setActiveTab] = useState("ai-visibility");
	// Only bumped by an explicit "go to settings" jump (below) — ordinary tab
	// clicks (onTabChange) must never touch this, or the Tabs subtree remounts
	// and silently discards unsaved SettingsTab editor state.
	const [settingsJumpCount, setSettingsJumpCount] = useState(0);

	const goToSettings = () => {
		setSettingsJumpCount((n) => n + 1);
		setActiveTab("settings");
	};

	return (
		<AppPage title="SEO" className="seo-page">
			<Tabs
				// remount only on a programmatic "go to settings" jump so it's
				// reflected even though defaultActiveKey only applies on mount;
				// a plain tab-trigger click must NOT change this key.
				key={`seo-tabs-settings-jump-${settingsJumpCount}`}
				defaultActiveKey={activeTab}
				onTabChange={setActiveTab}
				className="seo-tabs"
			>
				<Tabs.Panel eventKey="ai-visibility" title="AI Visibility">
					<AIVisibilityTab onGoToSettings={goToSettings} />
				</Tabs.Panel>
				<Tabs.Panel eventKey="settings" title="Settings">
					<SettingsTab />
				</Tabs.Panel>
			</Tabs>
		</AppPage>
	);
};

function PageSeoWebsiteCheck() {
	const company = useCompany();
	const navigate = useNavigate();
	const hasWebsite = !!company?.website;

	if (!hasWebsite) {
		return (
			<EmptyPage title="SEO">
				<StatusPlaceholder
					label="Add a website"
					sublabel="To use this feature, you need to add your company website."
					image={Svg.Data}
				>
					<Button
						onClick={() => navigate(LinkGen.Settings.CompanyInformation())}
						size="sm"
						icon="arrow_forward"
					>
						Company Information
					</Button>
				</StatusPlaceholder>
			</EmptyPage>
		);
	}

	return <PageSeoTabs />;
}

function PagePlanRequired() {
	const isPlanPaid = useBillingPlanPaidCheck();
	const navigate = useNavigate();

	if (!isPlanPaid) {
		return (
			<StatusPlaceholder
				label="Subscription is required"
				sublabel="To use this feature, you need an active subscription."
				image={Svg.Data}
			>
				<Button
					onClick={() => navigate(LinkGen.Settings.Billing())}
					size="sm"
					icon="arrow_forward"
				>
					Upgrade
				</Button>
			</StatusPlaceholder>
		);
	}

	return <PageSeoWebsiteCheck />;
}

const PageSeo: FC = () => <PagePlanRequired />;

export default PageSeo;
